import { ReactNode, useCallback, useState } from "react";
import { Platform, View } from "react-native";
import { RefreshControl, ScrollView } from "react-native-gesture-handler";
import Header from "src/components/Header";
import PillButton from "src/components/PillButton";
import ToggleBadge, { ToggleVariant } from "src/components/ToggleBadge";
import log from "src/helpers/log";
import useDevice from "src/hooks/useDevice";
import { enumNever } from "src/shared/helpers/generalHelpers";
import { radiusMedium } from "src/styles/radius";
import {
	paddingLarge,
	paddingMedium,
	paddingSmall,
	paddingXL,
	paddingXXL,
} from "src/styles/spacing";
import { Text, useSafeArea } from "src/swsh-native";
import GatheringFilterSortCard from "../components/GatheringFilterSortCard";
import GatheringFilterSortNumColumns from "../components/GatheringFilterSortNumColumns";
import GatheringFilterSortSectionHeader from "../components/GatheringFilterSortSectionHeader";
import useGatheringFilterSortOptions from "../hooks/useGatheringFilterSortOptions";
import useGatheringFilterSortStore from "../hooks/useGatheringFilterSortStore";
import { GatheringFilterSortCategorySection } from "../types";

const renderSectionOptions = (args: GatheringFilterSortCategorySection): ReactNode => {
	const { variant } = args;
	switch (variant) {
		case "SingleSelectList":
		case "MultiSelectList": {
			const { options = [] } = args;
			const toggleVariant: ToggleVariant =
				variant === "SingleSelectList" ? "SingleSelect" : "MultiSelect";
			return (
				<View
					style={{
						borderRadius: radiusMedium,
						overflow: "hidden",
					}}
				>
					{options.map(
						({ id, imageSrc, hasFallbackImage, text, onPress, isSelected }, index) => (
							<GatheringFilterSortCard
								key={id}
								imageSrc={imageSrc}
								hasFallbackImage={hasFallbackImage}
								text={text}
								onPress={onPress}
								renderSelector={() => (
									<ToggleBadge
										isSelected={isSelected ?? false}
										variant={toggleVariant}
									/>
								)}
								bottomBorder={index < options.length - 1}
							/>
						),
					)}
				</View>
			);
		}
		case "MultiSelectPill": {
			const { options = [] } = args;
			return (
				<View
					style={{
						flexDirection: "row",
						flexWrap: "wrap",
						columnGap: paddingMedium,
						rowGap: paddingMedium,
					}}
				>
					{options.map(({ id, Icon, emoji, text, onPress, isSelected }) => (
						// [TODO] M: new Pill variant for border on-select + size is different on Figma
						<PillButton
							testID={`FilterMisc-${id}`}
							sizeVariant="md"
							key={id}
							Icon={Icon}
							onPress={onPress}
							CustomIcon={
								emoji ? (
									<Text
										semibold
										xs
										style={{
											// Fixes clipping issue
											paddingTop:
												Platform.OS === "ios" ? paddingSmall : undefined,
											marginLeft: paddingLarge,
										}}
									>
										{emoji}
									</Text>
								) : undefined
							}
							style={{
								borderWidth: 1,
								borderColor: isSelected ? "#FFFFFF" : "#2B303B",
							}}
							textProps={{
								xs: true,
								semibold: true,
							}}
						>
							{text}
						</PillButton>
					))}
				</View>
			);
		}
		case "NumColumns": {
			return <GatheringFilterSortNumColumns />;
		}
		default:
			return enumNever(variant);
	}
};

const GatheringFilterSortMain = () => {
	const { native } = useDevice();
	const gatheringId = useGatheringFilterSortStore((state) => state.gatheringId);
	const closeSelf = useGatheringFilterSortStore((state) => state.closeSelf);
	const refreshTopic = useGatheringFilterSortStore((state) => state.refreshTopic);
	const { categories } = useGatheringFilterSortOptions({ gatheringId });
	const safeArea = useSafeArea();

	const [refreshing, setRefreshing] = useState(false);
	const handleRefresh = useCallback(async () => {
		setRefreshing(true);
		try {
			await refreshTopic.emitAsync("refresh", {});
		} catch (e) {
			log.error({ e }, "Failed to refresh");
		} finally {
			setRefreshing(false);
		}
	}, [refreshTopic]);

	return (
		<View
			style={{
				height: "100%",
				width: "100%",
				alignItems: "center",
			}}
		>
			<Header
				completeVariant="None"
				exitVariant={native ? "None" : "Close"}
				onExit={closeSelf}
				variant="Modal"
				keyboardControls
			>
				Sort & Filters
			</Header>
			<ScrollView
				style={{
					width: "100%",
				}}
				contentContainerStyle={{
					paddingHorizontal: paddingXL,
					rowGap: paddingXXL,
				}}
				showsVerticalScrollIndicator={false}
				refreshControl={
					<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
				}
			>
				{categories.map(
					({ label, sections }) =>
						sections.length > 0 && (
							<View
								testID={`FilterSortCategory-${label}`}
								key={label}
								style={{ width: "100%", rowGap: paddingLarge }}
							>
								<Text small semibold>
									{label}
								</Text>
								{sections.map((args) => {
									const { key } = args;

									return (
										<View testID={`FilterSortSection-${key}`} key={key}>
											{"headerProps" in args && args.headerProps && (
												<GatheringFilterSortSectionHeader
													{...args.headerProps}
												/>
											)}
											{renderSectionOptions(args)}
										</View>
									);
								})}
							</View>
						),
				)}
			</ScrollView>
		</View>
	);
};

export default GatheringFilterSortMain;
